import React, { useState, useCallback, useEffect } from "react";
import { Typography } from "@mui/material";
import { useLoadScript } from "@react-google-maps/api";
import config from "../../config";
import {
  fetchRoutesByOrganization,
  fetchStopsByOrganization,
  fetchVehiclesByOrganization,
  uploadStudentProfilePicture,
  upsertParent,
} from "./CreateStudentForm.api";
import { FormContainer } from "./CreateStudentForm.styles";
import CreateStudentFormView from "./CreateStudentFormView";
import {
  libraries,
  BANGALORE_AIRPORT,
  getDefaultFormData,
  normalizeInitialFormData,
  matchesStopSelection,
  resolveStopOptionValue,
  buildUniqueRouteStops,
  mapRouteCoordinatesToOrganizationStops,
  ensureStudentStopsInRouteStops,
  getVehiclesForRoute,
  getVehicleAssignmentDetails,
  validateField,
  validateAllFields,
  getParentNameParts,
  buildStudentData,
  getStopsToDisplay,
  getSelectedRouteColor,
} from "./CreateStudentForm.helpers";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useConfiguredGoogleMap from "../../hooks/useConfiguredGoogleMap.js";
import { useTranslation } from "react-i18next";

export default function CreateStudentForm({
  onClose,
  onSubmit,
  onDelete,
  onProfileImageDelete,
  initialData = null,
  isEdit = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const [organizationId, setOrganizationId] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [organizationStops, setOrganizationStops] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [availablePickupStops, setAvailablePickupStops] = useState([]);
  const [availableDropStops, setAvailableDropStops] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [imageError, setImageError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isStopDetailsLoading, setIsStopDetailsLoading] = useState(false);
  const [formData, setFormData] = useState(() =>
    normalizeInitialFormData(initialData),
  );
  const [imagePreview, setImagePreview] = useState(
    initialData?.profileImage || null,
  );
  const [map, setMap] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [directions, setDirections] = useState(null);
  const [errors, setErrors] = useState({});
  const { placeMarkers, getMapOptions } = useConfiguredGoogleMap({
    map,
    isLoaded,
  });

  useEffect(() => {
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        const orgId = userState.user?.organizationId;
        if (orgId) setOrganizationId(orgId);
      }
    } catch (err) {
      console.error("Error reading user state:", err);
    }
  }, []);

  useEffect(() => {
    if (organizationId) {
      fetchRoutes();
      fetchVehicles();
    }
  }, [organizationId]);

  const fetchRoutes = async () => {
    setIsStopDetailsLoading(true);
    try {
      const [routesData, stopsData] = await Promise.all([
        fetchRoutesByOrganization(organizationId),
        fetchStopsByOrganization(organizationId),
      ]);

      setOrganizationStops(stopsData);

      if (!routesData.length) {
        setRoutes([]);
        return;
      }

      const routesWithStops = await Promise.all(
        routesData.map(async (route) => ({
          ...route,
          stops: await fetchStopsForRoute(route, stopsData),
        })),
      );

      setRoutes(routesWithStops);
    } catch (error) {
      console.error("Error fetching routes:", error);
    } finally {
      setIsStopDetailsLoading(false);
    }
  };

  const fetchStopsForRoute = async (route, stopsData = organizationStops) => {
    if (!route.routeCoordinates || !Array.isArray(route.routeCoordinates)) {
      return [];
    }

    const mappedStops = mapRouteCoordinatesToOrganizationStops(
      route.routeCoordinates,
      stopsData,
    );

    return buildUniqueRouteStops(mappedStops);
  };

  const fetchVehicles = async () => {
    try {
      setVehicles(await fetchVehiclesByOrganization(organizationId));
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  const getSelectedVehicle = useCallback(
    (vehicleId) =>
      vehicles.find((vehicle) => String(vehicle.id) === String(vehicleId)) ||
      filteredVehicles.find(
        (vehicle) => String(vehicle.id) === String(vehicleId),
      ) ||
      null,
    [filteredVehicles, vehicles],
  );

  useEffect(() => {
    if (formData.routeId) {
      const selectedRoute = routes.find(
        (r) => r.id === parseInt(formData.routeId),
      );

      setAvailablePickupStops(
        ensureStudentStopsInRouteStops(
          selectedRoute?.stops || [],
          formData,
          initialData,
        ),
      );
      setFilteredVehicles(
        getVehiclesForRoute(
          formData.routeId,
          selectedRoute?.vehicles || [],
          vehicles,
          formData.vehicleId,
        ),
      );
    } else {
      setAvailablePickupStops([]);
      setFilteredVehicles([]);
    }
  }, [formData.routeId, formData.vehicleId, routes, vehicles, initialData]);

  useEffect(() => {
    if (formData.routeId) {
      const selectedRoute = routes.find(
        (r) => r.id === parseInt(formData.routeId),
      );
      const routeStops = ensureStudentStopsInRouteStops(
        selectedRoute?.stops || [],
        formData,
        initialData,
      );
      setAvailableDropStops(
        routeStops.filter(
          (stop) =>
            !matchesStopSelection(
              stop,
              formData.pickupStopId,
              formData.pickupStopName,
            ),
        ),
      );
    } else {
      setAvailableDropStops([]);
    }
  }, [
    formData.routeId,
    formData.pickupStopId,
    formData.pickupStopName,
    routes,
    initialData,
  ]);

  useEffect(() => {
    if (!initialData?.profileImage) {
      setImagePreview(null);
      return;
    }

    if (typeof initialData.profileImage === "string") {
      setImagePreview(initialData.profileImage);
    } else if (initialData.profileImage instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(initialData.profileImage);
    }
  }, [initialData]);

  useEffect(() => {
    setFormData(normalizeInitialFormData(initialData));
  }, [initialData]);

  useEffect(() => {
    if (!formData.vehicleId) {
      return;
    }

    const selectedVehicle = getSelectedVehicle(formData.vehicleId);
    if (!selectedVehicle) {
      return;
    }

    const assignmentDetails = getVehicleAssignmentDetails(selectedVehicle);

    if (
      assignmentDetails.driverName !== formData.driverName ||
      assignmentDetails.attendantName !== formData.attendantName ||
      assignmentDetails.driverId !== formData.driverId ||
      assignmentDetails.employeeId !== formData.employeeId
    ) {
      setFormData((prev) => ({
        ...prev,
        ...assignmentDetails,
      }));
    }
  }, [
    formData.vehicleId,
    formData.driverName,
    formData.attendantName,
    formData.driverId,
    formData.employeeId,
    getSelectedVehicle,
  ]);

  useEffect(() => {
    if (!formData.routeId) return;

    const selectedRoute = routes.find(
      (route) => route.id === parseInt(formData.routeId),
    );
    const routeStops = ensureStudentStopsInRouteStops(
      selectedRoute?.stops || [],
      formData,
      initialData,
    );
    if (!routeStops.length) return;

    const resolvedPickupStopId = resolveStopOptionValue(
      routeStops,
      formData.pickupStopId,
      formData.pickupStopName,
    );
    const resolvedDropStopId = resolveStopOptionValue(
      routeStops,
      formData.dropStopId,
      formData.dropStopName,
    );

    if (
      resolvedPickupStopId !== formData.pickupStopId ||
      resolvedDropStopId !== formData.dropStopId
    ) {
      setFormData((prev) => ({
        ...prev,
        pickupStopId: resolvedPickupStopId,
        dropStopId: resolvedDropStopId,
        pickupStopName:
          routeStops.find(
            (stop) => Number(stop.id) === Number(resolvedPickupStopId),
          )?.name || prev.pickupStopName,
        dropStopName:
          routeStops.find(
            (stop) => Number(stop.id) === Number(resolvedDropStopId),
          )?.name || prev.dropStopName,
      }));
    }
  }, [
    formData.routeId,
    formData.pickupStopId,
    formData.dropStopId,
    formData.pickupStopName,
    formData.dropStopName,
    routes,
  ]);

  useEffect(() => {
    if (
      isLoaded &&
      formData.routeId &&
      formData.pickupStopId &&
      formData.dropStopId &&
      map
    ) {
      const selectedRoute = routes.find(
        (r) => r.id === parseInt(formData.routeId),
      );
      const routeStops = ensureStudentStopsInRouteStops(
        selectedRoute?.stops || [],
        formData,
        initialData,
      );
      if (!routeStops.length) return;

      const pickupIndex = routeStops.findIndex((s) =>
        matchesStopSelection(s, formData.pickupStopId, formData.pickupStopName),
      );
      const dropIndex = routeStops.findIndex((s) =>
        matchesStopSelection(s, formData.dropStopId, formData.dropStopName),
      );

      if (pickupIndex === -1 || dropIndex === -1 || pickupIndex === dropIndex) {
        setDirections(null);
        return;
      }

      // ✅ Handle both directions — pickup→drop or drop→pickup
      const startIndex = Math.min(pickupIndex, dropIndex);
      const endIndex = Math.max(pickupIndex, dropIndex);

      // ✅ Use full routeCoordinates sliced between pickup and drop
      const allCoords = (selectedRoute?.routeCoordinates || []).sort(
        (a, b) => a.order - b.order,
      );

      const pickupStop = routeStops[startIndex];
      const dropStop = routeStops[endIndex];

      // Find where pickup and drop sit inside the full routeCoordinates
      const coordPickupIdx = allCoords.findIndex(
        (c) =>
          Math.abs(parseFloat(c.latitude) - parseFloat(pickupStop.latitude)) <
            0.0001 &&
          Math.abs(parseFloat(c.longitude) - parseFloat(pickupStop.longitude)) <
            0.0001,
      );
      const coordDropIdx = allCoords.findIndex(
        (c) =>
          Math.abs(parseFloat(c.latitude) - parseFloat(dropStop.latitude)) <
            0.0001 &&
          Math.abs(parseFloat(c.longitude) - parseFloat(dropStop.longitude)) <
            0.0001,
      );

      // Fall back to stops-only if coords not found
      const filteredCoords =
        coordPickupIdx !== -1 &&
        coordDropIdx !== -1 &&
        coordPickupIdx !== coordDropIdx
          ? allCoords.slice(
              Math.min(coordPickupIdx, coordDropIdx),
              Math.max(coordPickupIdx, coordDropIdx) + 1,
            )
          : routeStops.slice(startIndex, endIndex + 1).map((s) => ({
              latitude: s.latitude,
              longitude: s.longitude,
              isStop: true,
            }));

      if (filteredCoords.length < 2) {
        setDirections(null);
        return;
      }

      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(
            parseFloat(filteredCoords[0].latitude),
            parseFloat(filteredCoords[0].longitude),
          ),
          destination: new window.google.maps.LatLng(
            parseFloat(filteredCoords[filteredCoords.length - 1].latitude),
            parseFloat(filteredCoords[filteredCoords.length - 1].longitude),
          ),
          waypoints: filteredCoords.slice(1, -1).map((coord) => ({
            location: new window.google.maps.LatLng(
              parseFloat(coord.latitude),
              parseFloat(coord.longitude),
            ),
            stopover: coord.isStop || false, // ✅ same as dashboard
          })),
          optimizeWaypoints: false,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);

            const bounds = new window.google.maps.LatLngBounds();
            filteredCoords.forEach((c) =>
              bounds.extend({
                lat: parseFloat(c.latitude),
                lng: parseFloat(c.longitude),
              }),
            );
            map.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });
          } else {
            console.error(`Directions API error: ${status}`);
            setDirections(null);
          }
        },
      );
    } else {
      setDirections(null);
      if (map && !formData.routeId) {
        map.setCenter(BANGALORE_AIRPORT);
        map.setZoom(12);
      }
    }
  }, [
    isLoaded,
    formData.routeId,
    formData.pickupStopId,
    formData.dropStopId,
    formData.pickupStopName,
    formData.dropStopName,
    map,
    routes,
    initialData,
  ]);

  const onLoad = useCallback((mapInstance) => setMap(mapInstance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const updates = { [field]: value };
    setSubmitError("");

    setErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value, t),
    }));

    if (field === "routeId") {
      Object.assign(updates, {
        pickupStopId: "",
        pickupStopName: "",
        dropStopId: "",
        dropStopName: "",
        vehicleId: "",
        driverName: "",
        attendantName: "",
        driverId: null,
        employeeId: null,
      });
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.pickupStopId;
        delete newErrors.dropStopId;
        delete newErrors.vehicleId;
        return newErrors;
      });
    }

    if (field === "pickupStopId") {
      const selectedPickupStop = availablePickupStops.find(
        (stop) => String(stop.id) === String(value),
      );
      updates.pickupStopName = selectedPickupStop?.name || "";
      updates.dropStopId = "";
      updates.dropStopName = "";
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.dropStopId;
        return newErrors;
      });
    }

    if (field === "dropStopId") {
      const selectedDropStop = availableDropStops.find(
        (stop) => String(stop.id) === String(value),
      );
      updates.dropStopName = selectedDropStop?.name || "";
    }

    if (field === "vehicleId") {
      const selectedVehicle = getSelectedVehicle(value);
      if (selectedVehicle) {
        Object.assign(updates, getVehicleAssignmentDetails(selectedVehicle));
      } else {
        Object.assign(updates, {
          driverName: "",
          attendantName: "",
          driverId: null,
          employeeId: null,
        });
      }
    }

    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleImageUpload = (event) => {
    setImageError("");
    setSubmitError("");
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setImageError(
        t("Please upload a valid image file (JPEG, PNG, GIF, or WEBP)"),
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError(t("Image size should be less than 5MB"));
      return;
    }

    setFormData((prev) => ({ ...prev, profileImage: file }));
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setSubmitError("");
    const newErrors = validateAllFields(formData, t);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = document.querySelector("[data-field]");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      let parentId;

      try {
        const { parentFirstName, parentLastName } = getParentNameParts(
          formData.parentName,
        );
        const parentResponse = await upsertParent({
          parentId: formData.parentId,
          firstName: parentFirstName,
          lastName: parentLastName,
          phoneNumber: formData.parentPhone,
          organizationId,
        });
        parentId = parentResponse.parent.id;
      } catch (parentError) {
        console.error("Error upserting parent:", parentError);
        setSubmitError(
          `${t("Failed to save parent record")}: ${
            parentError.response?.data?.error || parentError.message
          }`,
        );
        return;
      }

      const studentData = buildStudentData(
        formData,
        parentId,
        initialData,
        isEdit,
      );

      try {
        let studentId = initialData?.id;

        if (!isEdit) {
          studentId = await onSubmit(studentData);
        } else {
          if (formData.profileImage instanceof File && studentId) {
            try {
              await uploadStudentProfilePicture(
                studentId,
                formData.profileImage,
              );
            } catch (imageError) {
              console.error("Profile picture upload failed:", imageError);
              setImageError(t("Profile picture upload failed"));
              return;
            }
          }
          await onSubmit(studentData);
        }

        if (!isEdit && formData.profileImage instanceof File && studentId) {
          try {
            await uploadStudentProfilePicture(studentId, formData.profileImage);
          } catch (imageError) {
            console.error("Profile picture upload failed:", imageError);
            setImageError(
              t("Student created but profile picture upload failed"),
            );
          }
        }

        if (!isEdit) {
          setFormData(getDefaultFormData());
          setImagePreview(null);
          setImageError("");
          setErrors({});
        }

        if (onClose) onClose();
      } catch (studentError) {
        console.error("Error creating/updating student:", studentError);
        setSubmitError(
          ` ${
            studentError.response?.data?.error ||
            studentError.response?.data?.message ||
            studentError.message
          }`,
        );
      }
    } catch (error) {
      console.error("General error in handleSubmit:", error);
      setSubmitError(
        ` ${
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
        }`,
      );
    }
  };

  const handleDeleteClick = () => setShowDeleteModal(true);
  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete) onDelete();
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };
  const handleProfileImageDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isEdit && initialData?.id && !(formData.profileImage instanceof File)) {
      try {
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: "StudentImage",
            associateEntityId: initialData.id,
            associatedWith: "Student",
          },
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting profile image:", error);
        return;
      }
    }

    setImagePreview(null);
    setFormData((prev) => ({ ...prev, profileImage: null }));

    if (onProfileImageDelete) {
      onProfileImageDelete();
    }
  };

  const handleParentView = () => {
    if (!formData.parentId) {
      setSubmitError(t("No parent information available for this student."));
      return;
    }
    setSubmitError("");
    navigate(`/parent-view?parentId=${formData.parentId}`); // ← navigate with parentId
  };
  // Get stops to display on map
  // const getStopsToDisplay = () => {
  //   if (!formData.routeId) return [];

  //   const selectedRoute = routes.find(
  //     (r) => r.id === parseInt(formData.routeId),
  //   );
  //   if (!selectedRoute || !selectedRoute.stops) return [];

  //   if (formData.pickupStopId && formData.dropStopId) {
  //     const pickupIndex = selectedRoute.stops.findIndex(
  //       (stop) => stop.id === parseInt(formData.pickupStopId),
  //     );
  //     const dropIndex = selectedRoute.stops.findIndex(
  //       (stop) => stop.id === parseInt(formData.dropStopId),
  //     );

  //     if (pickupIndex !== -1 && dropIndex !== -1 && pickupIndex < dropIndex) {
  //       return selectedRoute.stops.slice(pickupIndex, dropIndex + 1);
  //     }
  //   }

  //   return [];
  // };

  // // Get selected route color
  // const getSelectedRouteColor = () => {
  //   if (!formData.routeId) return "#4285F4";
  //   const selectedRoute = routes.find(
  //     (r) => r.id === parseInt(formData.routeId),
  //   );
  //   return selectedRoute?.routeColor || "#4285F4";
  // };

  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return (
      <FormContainer>
        <div style={{ padding: "20px", textAlign: "center" }}>
          <Typography color="error">
            {t("Error loading Google Maps. Please check your API key.")}
          </Typography>
        </div>
      </FormContainer>
    );
  }

  if (!isLoaded) {
    return (
      <FormContainer>
        <div style={{ padding: "20px", textAlign: "center" }}>
          <Typography>{t("Loading map...")}</Typography>
        </div>
      </FormContainer>
    );
  }

  return (
    <CreateStudentFormView
      isEdit={isEdit}
      onClose={onClose}
      onLoad={onLoad}
      onUnmount={onUnmount}
      handleImageUpload={handleImageUpload}
      handleChange={handleChange}
      handleDeleteClick={handleDeleteClick}
      handleDeleteCancel={handleDeleteCancel}
      handleDeleteConfirm={handleDeleteConfirm}
      handleSubmit={handleSubmit}
      onDelete={onDelete}
      showDeleteModal={showDeleteModal}
      imageError={imageError}
      submitError={submitError}
      imagePreview={imagePreview}
      formData={formData}
      errors={errors}
      isStopDetailsLoading={isStopDetailsLoading}
      routes={routes}
      filteredVehicles={filteredVehicles}
      availablePickupStops={availablePickupStops}
      availableDropStops={availableDropStops}
      directions={directions}
      selectedRouteColor={getSelectedRouteColor(formData.routeId, routes)}
      stopsToDisplay={getStopsToDisplay(formData, routes, initialData)}
      handleProfileImageDelete={handleProfileImageDelete}
      handleParentView={handleParentView}
      placeMarkers={placeMarkers}
      getMapOptions={getMapOptions}
    />
  );
}
